import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Download, Globe2, Home, MapPin, Plane, Users } from "lucide-react";

import BroadcastButton from "../components/BroadcastButton";
import EmployeeList from "../components/EmployeeList";
import EmployeeMap from "../components/EmployeeMap";
import KpiCard from "../components/KpiCard";
import { api, getToken } from "../lib/api";
import type {
  CurrentUser,
  EmployeeListResponse,
  EmployeeStatus,
  EmployeeSummary,
  StatsResponse,
} from "../lib/types";

const STATUS_OPTIONS: { value: EmployeeStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "home", label: "At home" },
  { value: "relocated", label: "Relocated" },
  { value: "in_transit", label: "In transit" },
  { value: "unknown", label: "Unknown" },
];

// Client-side-only "view" filters that aren't backed by a status column.
type ViewFilter = "none" | "silent_48h" | "family";

const SILENT_THRESHOLD_HOURS = 48;

export default function Dashboard() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [nationality, setNationality] = useState("");
  const [status, setStatus] = useState<EmployeeStatus | "all">("all");
  const [view, setView] = useState<ViewFilter>("none");
  const [initBusy, setInitBusy] = useState(false);
  const [initToast, setInitToast] = useState<string | null>(null);
  const [backfillBusy, setBackfillBusy] = useState(false);

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<CurrentUser>("/api/me"),
  });

  const stats = useQuery({
    queryKey: ["stats"],
    queryFn: () => api.get<StatsResponse>("/api/employees/stats"),
  });

  const employees = useQuery({
    queryKey: ["employees", search, country, nationality, status],
    queryFn: () =>
      api.get<EmployeeListResponse>(
        `/api/employees?${new URLSearchParams({
          ...(search ? { search } : {}),
          ...(country ? { country } : {}),
          ...(nationality ? { nationality } : {}),
          ...(status !== "all" ? { status } : {}),
        })}`,
      ),
  });

  const rawItems: EmployeeSummary[] = employees.data?.items ?? [];

  const items = useMemo(() => {
    if (view === "none") return rawItems;
    if (view === "family") return rawItems.filter((e) => e.family_count > 1);
    if (view === "silent_48h") {
      const cutoff = Date.now() - SILENT_THRESHOLD_HOURS * 3600 * 1000;
      return rawItems.filter((e) => {
        const t = e.current.last_seen_at;
        if (!t) return true;
        const ms = new Date(t).getTime();
        return Number.isFinite(ms) ? ms < cutoff : true;
      });
    }
    return rawItems;
  }, [rawItems, view]);

  const countryOptions = useMemo(() => {
    const set = new Set<string>();
    rawItems.forEach((e) => {
      if (e.current.country) set.add(e.current.country);
      if (e.home_country) set.add(e.home_country);
    });
    return Array.from(set).sort();
  }, [rawItems]);
  const nationalityOptions = useMemo(() => {
    const set = new Set<string>();
    rawItems.forEach((e) => e.nationality && set.add(e.nationality));
    return Array.from(set).sort();
  }, [rawItems]);

  const total = stats.data?.total ?? 0;
  const home = stats.data?.by_status.home ?? 0;
  const relocated = stats.data?.by_status.relocated ?? 0;
  const inTransit = stats.data?.by_status.in_transit ?? 0;
  const silent = stats.data?.silent_over_48h ?? 0;
  const family = stats.data?.family_total ?? 0;

  // Helper that applies a status filter and clears any "view" filter.
  function applyStatus(s: EmployeeStatus | "all") {
    setStatus(s);
    setView("none");
  }
  // Helper that applies a client-side view filter and clears the status one.
  function applyView(v: ViewFilter) {
    setView((cur) => (cur === v ? "none" : v));
    setStatus("all");
  }
  function clearAllFilters() {
    setStatus("all");
    setView("none");
    setCountry("");
    setNationality("");
    setSearch("");
  }

  const filterActive =
    status !== "all" ||
    view !== "none" ||
    !!country ||
    !!nationality ||
    !!search;

  return (
    <div className="space-y-3 sm:space-y-4 flex-1 flex flex-col min-h-0">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        <KpiCard
          label="Total"
          value={total}
          icon={<Globe2 size={20} />}
          hint="employees tracked"
          onClick={clearAllFilters}
          active={!filterActive}
        />
        <KpiCard
          label="At home"
          value={home}
          icon={<Home size={20} />}
          tone="success"
          onClick={() => applyStatus("home")}
          active={status === "home" && view === "none"}
        />
        <KpiCard
          label="Relocated"
          value={relocated}
          icon={<Globe2 size={20} />}
          tone="warn"
          onClick={() => applyStatus("relocated")}
          active={status === "relocated" && view === "none"}
        />
        <KpiCard
          label="In transit"
          value={inTransit}
          icon={<Plane size={20} />}
          tone="info"
          onClick={() => applyStatus("in_transit")}
          active={status === "in_transit" && view === "none"}
        />
        <KpiCard
          label="Silent > 48h"
          value={silent}
          icon={<AlertTriangle size={20} />}
          tone={silent > 0 ? "danger" : "default"}
          onClick={() => applyView("silent_48h")}
          active={view === "silent_48h"}
        />
        <KpiCard
          label="Family members"
          value={family}
          icon={<Users size={20} />}
          onClick={() => applyView("family")}
          active={view === "family"}
        />
      </div>

      <div className="rounded-xl bg-[var(--color-surface)]/60 ring-1 ring-[var(--color-border)] p-2 sm:px-3 sm:py-2 flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…"
          className="flex-1 min-w-[180px] basis-full sm:basis-auto rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 sm:py-1.5 text-slate-100 outline-none focus:ring-sky-500 text-sm"
        />
        <BroadcastButton
          countries={countryOptions}
          nationalities={nationalityOptions}
          employees={items}
        />
        <button
          onClick={async () => {
            const token = getToken();
            const r = await fetch("/api/exports/employees.csv", {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "travel-tracking-employees.csv";
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 hover:bg-slate-700 ring-1 ring-slate-700 text-slate-200 px-3 py-1.5 text-sm"
        >
          <Download size={14} /> <span className="hidden sm:inline">Export CSV</span>
          <span className="sm:hidden">CSV</span>
        </button>
        {me.data?.role === "super_admin" && (
          <button
            onClick={async () => {
              const ok = window.confirm(
                "Re-geocode every home address using the latest rules?\n\n" +
                  "This refreshes home pins for all employees (does NOT touch people who relocated or are in transit). Manual map overrides on individual employees are preserved.",
              );
              if (!ok) return;
              setInitBusy(true);
              setInitToast(null);
              try {
                const r = await api.post<{
                  total: number;
                  geocoded: number;
                  set_home_status: number;
                  failed_geocode: number;
                  skipped_relocated?: number;
                  skipped_manual?: number;
                }>("/api/admin/seed/initialize-locations?force=true");
                const parts = [
                  `Geocoded ${r.geocoded}`,
                  `refreshed ${r.set_home_status} home pins`,
                ];
                if (r.skipped_relocated) {
                  parts.push(`kept ${r.skipped_relocated} relocated/in-transit`);
                }
                if (r.skipped_manual) {
                  parts.push(`kept ${r.skipped_manual} manual pins`);
                }
                if (r.failed_geocode) {
                  parts.push(`${r.failed_geocode} unresolved`);
                }
                setInitToast(parts.join(" · "));
                qc.invalidateQueries({ queryKey: ["employees"] });
                qc.invalidateQueries({ queryKey: ["stats"] });
              } catch {
                setInitToast("Failed to initialize locations");
              } finally {
                setInitBusy(false);
                setTimeout(() => setInitToast(null), 8000);
              }
            }}
            disabled={initBusy}
            title="Re-geocode home addresses using latest rules. Preserves manual map pins."
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 hover:bg-slate-700 ring-1 ring-slate-700 text-slate-200 px-3 py-1.5 text-sm disabled:opacity-50"
          >
            <MapPin size={14} />
            <span className="hidden sm:inline">{initBusy ? "Initializing…" : "Re-geocode all"}</span>
            <span className="sm:hidden">{initBusy ? "…" : "Geocode"}</span>
          </button>
        )}
        {me.data?.role === "super_admin" && (
          <button
            onClick={async () => {
              const ok = window.confirm(
                "Backfill missing coordinates for current locations?\n\n" +
                  "Walks every employee whose last check-in is missing lat/lng but has an address/city/country, and fills the coordinates from the static city table. Status is never changed.",
              );
              if (!ok) return;
              setBackfillBusy(true);
              setInitToast(null);
              try {
                const r = await api.post<{
                  total: number;
                  resolved: number;
                  no_match: number;
                  skipped_already_set: number;
                }>("/api/admin/seed/backfill-current-coords");
                setInitToast(
                  `Backfilled ${r.resolved} · already had ${r.skipped_already_set} · ${r.no_match} unresolved`,
                );
                qc.invalidateQueries({ queryKey: ["employees"] });
                qc.invalidateQueries({ queryKey: ["stats"] });
              } catch {
                setInitToast("Failed to backfill coordinates");
              } finally {
                setBackfillBusy(false);
                setTimeout(() => setInitToast(null), 8000);
              }
            }}
            disabled={backfillBusy}
            title="Fill missing coordinates on current locations from textual address/city/country. Does not change status."
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 hover:bg-slate-700 ring-1 ring-slate-700 text-slate-200 px-3 py-1.5 text-sm disabled:opacity-50"
          >
            <MapPin size={14} />
            <span className="hidden sm:inline">{backfillBusy ? "Backfilling…" : "Backfill coords"}</span>
            <span className="sm:hidden">{backfillBusy ? "…" : "Backfill"}</span>
          </button>
        )}
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-2 py-1.5 text-sm text-slate-200"
        >
          <option value="">All countries</option>
          {countryOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={nationality}
          onChange={(e) => setNationality(e.target.value)}
          className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-2 py-1.5 text-sm text-slate-200"
        >
          <option value="">All nationalities</option>
          {nationalityOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as EmployeeStatus | "all");
            setView("none");
          }}
          className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-2 py-1.5 text-sm text-slate-200"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        {filterActive && (
          <button
            onClick={clearAllFilters}
            className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1.5 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {initToast && (
        <div className="rounded-md bg-sky-500/10 ring-1 ring-sky-400/30 px-3 py-2 text-sm text-sky-100">
          {initToast}
        </div>
      )}

      {/* Fixed viewport-relative height so the employee list scrolls inside
          the panel instead of stretching the whole page. Map + list share
          the same height on desktop; stacked split on mobile. */}
      <section
        className={
          "grid min-h-[320px] gap-3 sm:gap-4 " +
          "h-[clamp(420px,calc(100dvh-12.25rem),900px)] " +
          "grid-cols-1 grid-rows-[minmax(200px,42fr)_minmax(0,58fr)] " +
          "lg:grid-cols-[3fr_2fr] lg:grid-rows-1"
        }
      >
        <div className="min-h-0 overflow-hidden rounded-xl ring-1 ring-[var(--color-border)]">
          <EmployeeMap items={items} />
        </div>
        <div className="min-h-0 overflow-y-auto overscroll-y-contain rounded-xl bg-[var(--color-surface)]/40 ring-1 ring-[var(--color-border)] p-2 [-webkit-overflow-scrolling:touch]">
          {employees.isPending ? (
            <div className="text-sm text-slate-400 p-4">Loading employees…</div>
          ) : (
            <EmployeeList items={items} />
          )}
        </div>
      </section>
    </div>
  );
}
